import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import AppBrandMark from '../AppBrandMark';
import MicrosoftButton from './MicrosoftButton';
import { friendlyAuthError } from '../../services/careAuthService';
import { setSignOutReason } from '../../hooks/useSessionGuard';
import './Security.css';

/**
 * Full-screen gate for Trilogy staff bootstrapped with a one-time password
 * (users/{uid}.authMethod === 'microsoft_pending'). Links their Microsoft work
 * account, after which the backend removes the password and the account can
 * only be entered through Microsoft + Duo.
 */
export default function ConnectMicrosoftScreen() {
  const { profile, user, signOut, linkMicrosoft } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const name = profile?.firstName || profile?.displayName || 'there';

  const handleLink = async () => {
    setError('');
    setBusy(true);
    try {
      await linkMicrosoft();
      setDone(true);
      // The password provider is gone and the current session was not verified by
      // Duo, so a fresh Microsoft sign-in is required for the idpMfa session claim.
      setTimeout(async () => {
        setSignOutReason('microsoft-linked');
        await signOut();
      }, 1800);
    } catch (err) {
      setError(friendlyAuthError(err, 'Could not connect your Microsoft account.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sec-screen">
      <div className="sec-card">
        <h1 className="sec-brand"><AppBrandMark size="login" /></h1>
        <h2 className="sec-title">Connect your Microsoft account</h2>
        {done ? (
          <div className="sec-success" role="status">
            Connected. Taking you back to the sign-in page — choose <strong>Sign in with Microsoft</strong> next time.
          </div>
        ) : (
          <>
            <p className="sec-desc">
              Hi {name} — Trilogy staff sign in to Trilogy One Care with their Microsoft work account
              (the same one you use for email, protected by Duo). Connect it now; your one-time password
              stops working after this step.
            </p>
            <p className="sec-desc">
              When the Microsoft window opens, choose <strong>{user?.email}</strong> and complete Duo if prompted.
            </p>
            {error && <div className="sec-error" role="alert">{error}</div>}
            <MicrosoftButton onClick={handleLink} disabled={busy}>
              {busy ? 'Connecting…' : 'Connect Microsoft account'}
            </MicrosoftButton>
          </>
        )}
        <div className="sec-footer">
          <button type="button" className="sec-link sec-link--muted" onClick={signOut} disabled={busy}>Sign out</button>
          <span className="sec-desc" style={{ margin: 0, fontSize: '0.8rem' }}>Need help? Contact your administrator.</span>
        </div>
      </div>
    </div>
  );
}
